import { Checkbox, Stack, Typography } from '@mui/material';
import { _ } from '/@/locale';

/**
 * Sections of the v2 PDF a user can include, mirroring `RECOMMENDATION_PDF_BLOCKS` on the
 * backend — the order here is the order of the checkboxes in the design.
 *
 * `Datos generales` and the hero aren't listed: they identify the recommendation, so they are
 * always rendered.
 */
export const PDF_BLOCKS = [
    {
        key: 'hybrids',
        label: () => _('Híbridos recomendados'),
        description: () => _('Rendimiento estimado, densidad y bolsas totales.'),
    },
    {
        key: 'density_curve',
        label: () => _('Curva de densidad'),
        description: () => _('Gráfico indicando el rendimiento y densidad.'),
    },
    {
        key: 'density_simulation',
        label: () => _('Simulación de densidad y ambiente'),
        description: () => _('Rendimiento de la densidad recomendada y la habitual según el ambiente.'),
    },
    {
        key: 'phenology',
        label: () => _('Fenología'),
        description: () => _('Estadios y período crítico según la fecha de siembra.'),
    },
    {
        key: 'nutrients',
        label: () => _('Requerimiento nutricional'),
        description: () => _('Nutrientes totales necesarios.'),
    },
    {
        key: 'defensive_profile',
        label: () => _('Perfil defensivo'),
        description: () => _('Comportamiento frente a enfermedades e incidencias'),
    },
    {
        key: 'characteristics',
        label: () => _('Características del híbrido'),
        description: () => _('Resumen técnico de cada híbrido.'),
    },
    {
        key: 'refuge',
        label: () => _('Refugio'),
        description: () => _('Información y productos recomendados para el refugio.'),
    },
] as const;

export type PdfBlockKey = (typeof PDF_BLOCKS)[number]['key'];

/** The blocks ticked when the picker opens, per the design. */
export const DEFAULT_PDF_BLOCKS: PdfBlockKey[] = [
    'hybrids',
    'density_curve',
    'density_simulation',
    'phenology',
    'nutrients',
];

/**
 * The blocks this recommendation can actually render. The density simulation compares against
 * the grower's habitual density, so without one it has no table to show and isn't offered.
 */
export const availablePdfBlocks = ({ hasUserDensity }: { hasUserDensity: boolean }): PdfBlockKey[] =>
    PDF_BLOCKS.map((block) => block.key).filter((key) => key !== 'density_simulation' || hasUserDensity);

/**
 * Whether the recommendation has the habitual density the density-simulation block needs. The
 * PDF renders from `{ ...recommendation, ...recommendation.backup }` (see
 * `useMyRecommendationPDF`), so this reads the same merge: a raw management row has `crops: []`
 * and keeps its crop config in `backup`.
 */
export const hasPdfUserDensity = (recommendation: { crops?: unknown[]; backup?: unknown }): boolean => {
    const merged = { ...recommendation, ...(recommendation.backup as { crops?: unknown[] } | undefined) };
    return !!(merged.crops?.[0] as { userDensity?: number | null } | undefined)?.userDensity;
};

/**
 * The block picker, shared by the "Generar PDF" and "Compartir" flows — they differ only in
 * their title and footer, which the popup supplies.
 */
function PdfBlocksStep({
    selected,
    setSelected,
    available,
}: {
    selected: PdfBlockKey[];
    setSelected: (blocks: PdfBlockKey[]) => void;
    /** Blocks to list; every block when omitted. */
    available?: PdfBlockKey[];
}) {
    const toggle = (key: PdfBlockKey) =>
        setSelected(selected.includes(key) ? selected.filter((block) => block !== key) : [...selected, key]);

    return (
        <Stack spacing={2} width='100%'>
            <Typography variant='body1' color='primary' textAlign='left'>
                {_('Elegí los bloques que queres incluir en el PDF')}
            </Typography>
            <Stack spacing={1} width='100%'>
                {PDF_BLOCKS.filter((block) => !available || available.includes(block.key)).map((block) => {
                    const isSelected = selected.includes(block.key);
                    return (
                        <Stack
                            key={block.key}
                            direction='row'
                            alignItems='flex-start'
                            spacing={1}
                            onClick={() => toggle(block.key)}
                            data-testid={`pdf-block-${block.key}`}
                            sx={{
                                cursor: 'pointer',
                                p: 1,
                                borderRadius: '4px',
                                border: '1px solid',
                                // A selected block reads as a filled card, as in the design.
                                borderColor: (theme) => (isSelected ? theme.palette.secondary.main : 'divider'),
                                backgroundColor: (theme) => (isSelected ? theme.palette.grey[100] : 'transparent'),
                            }}
                        >
                            <Checkbox checked={isSelected} color='secondary' size='large' />
                            <Stack textAlign='left'>
                                <Typography variant='subtitle1' fontWeight={700} color='primary'>
                                    {block.label()}
                                </Typography>
                                <Typography variant='body2' color='primary.mui.inactive'>
                                    {block.description()}
                                </Typography>
                            </Stack>
                        </Stack>
                    );
                })}
            </Stack>
        </Stack>
    );
}

export default PdfBlocksStep;
